import { useState, useCallback, useEffect } from 'react';
import api from '../../../services/api';

/**
 * Hook för att hantera mötesanteckningar
 *
 * @param {string} meetingId - ID för mötet (kan vara null initialt)
 */
export function useMeetingNotes(meetingId) {
  const [notes, setNotes] = useState([]);
  const [counts, setCounts] = useState({ total: 0, shared: 0, internal: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Hämta anteckningar
  const fetchNotes = useCallback(async () => {
    if (!meetingId) {
      setNotes([]);
      setCounts({ total: 0, shared: 0, internal: 0 });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/meeting-notes/meeting/${meetingId}`);
      setNotes(response.data.data?.notes || []);
      setCounts(response.data.data?.counts || { total: 0, shared: 0, internal: 0 });
    } catch (err) {
      console.error('Fel vid hämtning av anteckningar:', err);
      setError(err.response?.data?.message || 'Kunde inte hämta anteckningar');
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  // Hämta vid mount och när meetingId ändras
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Skapa anteckning
  const createNote = useCallback(async ({
    content,
    inputMethod = 'text',
    voiceConfidence,
    visibility = 'internal',
    isActionPoint = false
  }) => {
    if (!meetingId) {
      throw new Error('Inget möte valt');
    }

    try {
      const response = await api.post('/meeting-notes', {
        meetingId,
        content,
        inputMethod,
        voiceConfidence,
        visibility,
        isActionPoint
      });
      const newNote = response.data.data;
      setNotes(prev => [newNote, ...prev]);
      setCounts(prev => ({
        total: prev.total + 1,
        shared: visibility === 'shared' ? prev.shared + 1 : prev.shared,
        internal: visibility === 'internal' ? prev.internal + 1 : prev.internal
      }));
      return newNote;
    } catch (err) {
      console.error('Fel vid skapande av anteckning:', err);
      throw err;
    }
  }, [meetingId]);

  // Uppdatera anteckning
  const updateNote = useCallback(async (noteId, content, room) => {
    try {
      const response = await api.put(`/meeting-notes/${noteId}`, { content, room });
      const updatedNote = response.data.data;
      setNotes(prev => prev.map(n => n._id === noteId ? updatedNote : n));
      return updatedNote;
    } catch (err) {
      console.error('Fel vid uppdatering av anteckning:', err);
      throw err;
    }
  }, []);

  // Ta bort anteckning
  const deleteNote = useCallback(async (noteId) => {
    try {
      const note = notes.find(n => n._id === noteId);
      await api.delete(`/meeting-notes/${noteId}`);
      setNotes(prev => prev.filter(n => n._id !== noteId));
      if (note) {
        setCounts(prev => ({
          total: prev.total - 1,
          shared: note.visibility === 'shared' ? prev.shared - 1 : prev.shared,
          internal: note.visibility === 'internal' ? prev.internal - 1 : prev.internal
        }));
      }
      return true;
    } catch (err) {
      console.error('Fel vid borttagning av anteckning:', err);
      throw err;
    }
  }, [notes]);

  // Ändra synlighet
  const updateVisibility = useCallback(async (noteId, visibility) => {
    try {
      const note = notes.find(n => n._id === noteId);
      const oldVisibility = note?.visibility;

      const response = await api.patch(`/meeting-notes/${noteId}/visibility`, { visibility });
      const updatedNote = response.data.data;
      setNotes(prev => prev.map(n => n._id === noteId ? updatedNote : n));

      // Uppdatera counts
      if (oldVisibility !== visibility) {
        setCounts(prev => ({
          total: prev.total,
          shared: visibility === 'shared'
            ? prev.shared + 1
            : prev.shared - 1,
          internal: visibility === 'internal'
            ? prev.internal + 1
            : prev.internal - 1
        }));
      }

      return updatedNote;
    } catch (err) {
      console.error('Fel vid ändring av synlighet:', err);
      throw err;
    }
  }, [notes]);

  // Ändra actionpunkt-status
  const updateActionPoint = useCallback(async (noteId, isActionPoint) => {
    try {
      const response = await api.patch(`/meeting-notes/${noteId}/action-point`, { isActionPoint });
      const updatedNote = response.data.data;
      setNotes(prev => prev.map(n => n._id === noteId ? updatedNote : n));
      return updatedNote;
    } catch (err) {
      console.error('Fel vid ändring av actionpunkt-status:', err);
      throw err;
    }
  }, []);

  return {
    notes,
    counts,
    loading,
    error,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
    updateVisibility,
    updateActionPoint
  };
}

export default useMeetingNotes;
